import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout.js";

function timeAgo(date) {
  const diff = Math.floor((Date.now() - new Date(date)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminMessagesScreen() {
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchMessages();
  }, []);

const fetchMessages = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("https://eleven11-i6v4.onrender.com/api/messages", {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });


      const data = await res.json();
      setMessages(data.messages || []);
      setUnread(data.unread || 0);
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    } finally {
      setLoading(false);
    }
  };

  const openMessage = async (msg) => {
    setSelected(msg);
    if (!msg.read) {
      try {
      const token = localStorage.getItem("token");
        await fetch(`https://eleven11-i6v4.onrender.com/api/messages/${msg._id}/read`, {
          method: "PATCH",
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        setMessages(prev => prev.map(m => m._id === msg._id ? { ...m, read: true } : m));
        setUnread(prev => Math.max(0, prev - 1));
      } catch {}
    }
  };

  const deleteMessage = async (id) => {
    try {
  const token = localStorage.getItem("token");
      await fetch(`https://eleven11-i6v4.onrender.com/api/messages/${id}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      
      setMessages(prev => prev.filter(m => m._id !== id));
      if (selected?._id === id) setSelected(null);
    } catch {}
  };

  const filtered = messages.filter(msg => {
    const matchSearch = !search ||
      msg.name?.toLowerCase().includes(search.toLowerCase()) ||
      msg.email?.toLowerCase().includes(search.toLowerCase()) ||
      msg.company?.toLowerCase().includes(search.toLowerCase()) ||
      msg.message?.toLowerCase().includes(search.toLowerCase());

    const matchFilter =
      filter === "all" ||
      (filter === "unread" && !msg.read) ||
      (filter === "read" && msg.read);

    return matchSearch && matchFilter;
  });

  return (
    <AdminLayout>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <p style={styles.kicker}>ELEVEN CONTROL CENTER</p>
          <h1 style={styles.title}>
            Messages
            {unread > 0 && (
              <span style={styles.unreadBadge}>{unread} new</span>
            )}
          </h1>
        </div>
        <div style={styles.headerStats}>
          <div style={styles.statChip}>
            <span style={styles.statNum}>{messages.length}</span>
            <span style={styles.statLabel}>Total</span>
          </div>
          <div style={{ ...styles.statChip, ...(unread > 0 ? styles.statChipAlert : {}) }}>
            <span style={{ ...styles.statNum, color: unread > 0 ? "#DC2626" : "#0f0f17" }}>{unread}</span>
            <span style={styles.statLabel}>Unread</span>
          </div>
        </div>
      </div>

      <div style={styles.layout}>

        {/* Message List */}
        <div style={styles.listSection}>

          <div style={styles.toolbar}>
            <input
              style={styles.search}
              placeholder="Search messages..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div style={styles.filters}>
              {[
                { key: "all", label: "All" },
                { key: "unread", label: "Unread" },
                { key: "read", label: "Read" }
              ].map(f => (
                <button
                  key={f.key}
                  style={{ ...styles.filterBtn, ...(filter === f.key ? styles.filterBtnActive : {}) }}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div style={styles.listBody}>
            {loading && <p style={styles.empty}>Loading messages...</p>}
            {!loading && filtered.length === 0 && (
              <p style={styles.empty}>No messages found.</p>
            )}

            {!loading && filtered.map(msg => (
              <div
                key={msg._id}
                style={{
                  ...styles.messageRow,
                  ...(selected?._id === msg._id ? styles.messageRowSelected : {}),
                  ...(msg.read ? {} : styles.messageRowUnread)
                }}
                onClick={() => openMessage(msg)}
              >
                {/* Avatar */}
                <div style={{
                  ...styles.avatar,
                  background: msg.read ? "#f3f4f6" : "linear-gradient(135deg, #6366f1, #8b5cf6)"
                }}>
                  <span style={{ color: msg.read ? "#9ca3af" : "#fff", fontSize: 14, fontWeight: 800 }}>
                    {msg.name?.[0]?.toUpperCase() || "?"}
                  </span>
                </div>

                {/* Content */}
                <div style={styles.messageMain}>
                  <div style={styles.messageTop}>
                    <p style={{ ...styles.messageName, fontWeight: msg.read ? 500 : 800 }}>
                      {msg.name}
                      {msg.company && <span style={styles.messageCompany}> · {msg.company}</span>}
                    </p>
                    <span style={styles.messageTime}>{timeAgo(msg.createdAt)}</span>
                  </div>
                  <p style={styles.messageEmail}>{msg.email}</p>
                  <p style={{ ...styles.messagePreview, fontWeight: msg.read ? 400 : 600 }}>
                    {msg.message?.slice(0, 80)}{msg.message?.length > 80 ? "..." : ""}
                  </p>
                </div>

                {/* Unread dot */}
                {!msg.read && <div style={styles.unreadDot} />}
              </div>
            ))}
          </div>

        </div>

        {/* Message Detail */}
        {selected && (
          <div style={styles.detailPanel}>

            <div style={styles.detailHeader}>
              <div style={styles.detailAvatar}>
                {selected.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div style={{ flex: 1 }}>
                <p style={styles.detailName}>{selected.name}</p>
                {selected.company && <p style={styles.detailCompany}>{selected.company}</p>}
                <p style={styles.detailTime}>{new Date(selected.createdAt).toLocaleString("en-US", {
                  month: "long", day: "numeric", year: "numeric",
                  hour: "numeric", minute: "2-digit"
                })}</p>
              </div>
              <button style={styles.closeBtn} onClick={() => setSelected(null)}>✕</button>
            </div>

            {/* Contact Info */}
            <div style={styles.detailSection}>
              <p style={styles.detailLabel}>CONTACT INFO</p>
              <div style={styles.contactRow}>
                <span style={styles.contactKey}>Email</span>
                <a href={`mailto:${selected.email}`} style={styles.contactLink}>{selected.email}</a>
              </div>
              {selected.company && (
                <div style={styles.contactRow}>
                  <span style={styles.contactKey}>Company</span>
                  <span style={styles.contactVal}>{selected.company}</span>
                </div>
              )}

{selected.phone && (
  <div style={styles.contactRow}>
    <span style={styles.contactKey}>Phone</span>
    <a href={`tel:${selected.phone}`} style={styles.contactLink}>{selected.phone}</a>
  </div>
)}



            </div>

            {/* Message */}
            <div style={styles.detailSection}>
              <p style={styles.detailLabel}>MESSAGE</p>
              <p style={styles.messageBody}>{selected.message}</p>
            </div>

            {/* Reply Actions */}
            <div style={styles.detailSection}>
              <p style={styles.detailLabel}>REPLY</p>
              <a
                href={`mailto:${selected.email}?subject=Re: Your ELEVEN Inquiry`}
                style={styles.replyBtn}
              >
                ✉ Reply via Email
              </a>
            </div>

            {/* Delete */}
            <div style={{ padding: "16px 20px" }}>
              <button
                style={styles.deleteBtn}
                onClick={() => deleteMessage(selected._id)}
              >
                Delete Message
              </button>
            </div>

          </div>
        )}

      </div>

    </AdminLayout>
  );
}

const styles = {

  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 28
  },

  kicker: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 2,
    color: "#9ca3af",
    textTransform: "uppercase"
  },

  title: {
    margin: "6px 0 0",
    fontSize: 36,
    fontWeight: 800,
    letterSpacing: -1,
    color: "#0f0f17",
    display: "flex",
    alignItems: "center",
    gap: 12
  },

  unreadBadge: {
    fontSize: 13,
    fontWeight: 700,
    background: "#DC2626",
    color: "#fff",
    padding: "3px 10px",
    borderRadius: 999
  },

  headerStats: {
    display: "flex",
    gap: 12,
    paddingTop: 8
  },

  statChip: {
    background: "#fff",
    borderRadius: 12,
    padding: "10px 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
  },

  statChipAlert: {
    background: "#FFF0F0",
    border: "1px solid #FECACA"
  },

  statNum: {
    fontSize: 22,
    fontWeight: 900,
    color: "#0f0f17",
    lineHeight: 1
  },

  statLabel: {
    fontSize: 11,
    color: "#9ca3af",
    fontWeight: 600
  },

  layout: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 20,
    alignItems: "start"
  },

  listSection: {
    background: "#fff",
    borderRadius: 18,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)"
  },

  toolbar: {
    padding: "16px 24px",
    borderBottom: "1px solid #f3f4f6",
    display: "flex",
    gap: 12,
    alignItems: "center"
  },

  search: {
    flex: 1,
    padding: "10px 14px",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    fontSize: 14,
    outline: "none"
  },

  filters: {
    display: "flex",
    gap: 6
  },

  filterBtn: {
    padding: "7px 14px",
    border: "1px solid #e5e7eb",
    borderRadius: 999,
    background: "transparent",
    fontSize: 12,
    fontWeight: 600,
    color: "#6b7280",
    cursor: "pointer"
  },

  filterBtnActive: {
    background: "#0f0f17",
    color: "#fff",
    border: "1px solid #0f0f17"
  },

  listBody: {
    maxHeight: "calc(100vh - 260px)",
    overflowY: "auto"
  },

  empty: {
    textAlign: "center",
    color: "#9ca3af",
    padding: "40px 0",
    fontSize: 14
  },

  messageRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    padding: "16px 24px",
    borderBottom: "1px solid #f9fafb",
    cursor: "pointer",
    transition: "background 0.1s ease",
    position: "relative"
  },

  messageRowSelected: {
    background: "#f5f3ff"
  },

  messageRowUnread: {
    background: "#fafafe"
  },

  avatar: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },

  messageMain: {
    flex: 1,
    overflow: "hidden"
  },

  messageTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8
  },

  messageName: {
    margin: 0,
    fontSize: 14,
    color: "#0f0f17"
  },

  messageCompany: {
    fontWeight: 400,
    color: "#9ca3af"
  },

  messageTime: {
    fontSize: 12,
    color: "#9ca3af",
    flexShrink: 0
  },

  messageEmail: {
    margin: "2px 0 4px",
    fontSize: 12,
    color: "#9ca3af"
  },

  messagePreview: {
    margin: 0,
    fontSize: 13,
    color: "#6b7280",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },

  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#6366f1",
    flexShrink: 0,
    marginTop: 6
  },

  /* Detail Panel */
  detailPanel: {
    width: 380,
    background: "#fff",
    borderRadius: 18,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)",
    maxHeight: "calc(100vh - 160px)",
    overflowY: "auto",
    position: "sticky",
    top: 20
  },

  detailHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    padding: "20px 20px 16px",
    borderBottom: "1px solid #f3f4f6"
  },

  detailAvatar: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    fontWeight: 800,
    flexShrink: 0
  },

  detailName: {
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
    color: "#0f0f17"
  },

  detailCompany: {
    margin: "2px 0 0",
    fontSize: 13,
    color: "#9ca3af"
  },

  detailTime: {
    margin: "4px 0 0",
    fontSize: 12,
    color: "#9ca3af"
  },

  closeBtn: {
    border: "none",
    background: "#f3f4f6",
    borderRadius: "50%",
    width: 28,
    height: 28,
    cursor: "pointer",
    fontSize: 13,
    color: "#6b7280",
    flexShrink: 0
  },

  detailSection: {
    padding: "16px 20px",
    borderBottom: "1px solid #f3f4f6"
  },

  detailLabel: {
    margin: "0 0 10px",
    fontSize: 10,
    fontWeight: 900,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: 1.5
  },

  contactRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 0",
    borderBottom: "1px solid #f9fafb"
  },

  contactKey: {
    fontSize: 13,
    color: "#9ca3af",
    fontWeight: 500
  },

  contactLink: {
    fontSize: 13,
    color: "#6366f1",
    fontWeight: 600,
    textDecoration: "none"
  },

  contactVal: {
    fontSize: 13,
    color: "#374151",
    fontWeight: 600
  },

  messageBody: {
    margin: 0,
    fontSize: 14,
    color: "#374151",
    lineHeight: "24px",
    whiteSpace: "pre-wrap"
  },

  replyBtn: {
    display: "block",
    width: "100%",
    padding: "12px",
    border: "none",
    borderRadius: 10,
    background: "#0f0f17",
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "center",
    textDecoration: "none",
    boxSizing: "border-box"
  },

  deleteBtn: {
    width: "100%",
    padding: "10px",
    border: "1px solid #fecaca",
    borderRadius: 10,
    background: "transparent",
    color: "#DC2626",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer"
  }
};